'use client'

import { useActionState, useId, useState } from 'react'
import { Lock, Mail } from 'lucide-react'
import { Button, Checkbox, Field, Icon, Input, Pending } from '@/components/ui'
import { adresseInterne } from '@/lib/auth/roles'
import { connexion, type EtatFormulaire } from '../actions'
import { EnTeteVerre, LienVerre } from '../verre'

/**
 * Formulaire de connexion.
 *
 * L'indicateur d'aiguillage est **cosmétique**. Il reprend le geste de la
 * maquette — l'espace se devine à mesure qu'on tape — mais il ne décide rien :
 * la destination est choisie par le serveur à partir du rôle porté par le
 * compte.
 *
 * Il ne nomme d'ailleurs jamais l'espace interne précis. La maquette le déduit
 * du prénom pour les besoins de la démonstration ; le faire ici publierait
 * l'organigramme d'Umiros à quiconque tape une adresse au hasard.
 *
 * ------------------------------------------------------------------------
 * Passage sur la carte de verre — ce qui a bougé, et ce qui n'a pas bougé
 * ------------------------------------------------------------------------
 * **Rien de la logique.** L'action serveur, `useActionState`, les `name=`,
 * l'aiguillage par domaine, les trois libellés de `decrireAiguillage`, la garde
 * anti-énumération, l'état `enCours` : identiques au caractère près.
 *
 * Ce qui a bougé est la seule présentation : le ton des champs (`ton="fonce"`),
 * les couleurs des trois états de l'indicateur, la variante du bouton, et les
 * liens qui passent du lavande foncé au crème (voir `verre.tsx`).
 */
export function FormulaireConnexion({ suite }: { suite?: string }) {
  const [etat, action, enCours] = useActionState<EtatFormulaire, FormData>(connexion, {})
  const [email, setEmail] = useState('')
  const idEmail = useId()
  const idMotDePasse = useId()

  const indicateur = decrireAiguillage(email)

  return (
    <form action={action} className="flex flex-col gap-5">
      <EnTeteVerre surtitre="Bon retour" titre="Se" accent="connecter">
        Une seule adresse pour tout Umiros — votre espace s&apos;ouvre selon votre profil.
      </EnTeteVerre>

      {suite && <input type="hidden" name="suite" value={suite} />}

      <Field
        label="Adresse e-mail"
        htmlFor={idEmail}
        ton="fonce"
        error={etat.champ === 'email' ? etat.erreur : undefined}
      >
        <Input
          id={idEmail}
          name="email"
          type="email"
          autoComplete="email"
          required
          placeholder="vous@exemple.fr"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          invalid={etat.champ === 'email'}
          ton="fonce"
          icone={<Icon as={Mail} />}
        />
      </Field>

      {/* `aria-live="polite"` : le changement est annoncé sans interrompre la
          saisie en cours. */}
      <div
        aria-live="polite"
        className={`rounded-[18px] border p-3.5 transition-colors duration-(--dur-base) ease-(--ease-umiros) ${indicateur.classe}`}
      >
        <p className="flex items-center gap-2.5 text-[13.5px] font-bold text-cream">
          <span className={`size-1.5 shrink-0 rounded-pill ${indicateur.point}`} aria-hidden="true" />
          {indicateur.titre}
        </p>
        <p className="mt-1 pl-[18px] text-[12.5px] text-cream/75">{indicateur.detail}</p>
      </div>

      <Field
        label="Mot de passe"
        htmlFor={idMotDePasse}
        ton="fonce"
        error={etat.champ === 'motDePasse' ? etat.erreur : undefined}
      >
        <Input
          id={idMotDePasse}
          name="motDePasse"
          type="password"
          autoComplete="current-password"
          required
          invalid={etat.champ === 'motDePasse'}
          ton="fonce"
          icone={<Icon as={Lock} />}
        />
      </Field>

      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* La case est lue par `connexion()` : cochée, la session dure 30 jours ;
            décochée, le cookie meurt à la fermeture du navigateur. Elle était
            purement décorative jusqu'ici — affichée, cochée d'avance, et jamais
            consultée. Une commande qui ment sur ce qu'elle fait est pire qu'une
            commande absente, en particulier sur un poste partagé.

            ⚠️ Aucune marge en `className` sur cette case : en ton sombre elle
            porte `py-[13px] -my-[13px]` pour monter à 44px de cible tactile, et
            `Field.tsx` documente qu'un `mt-*` l'emporterait sur le `-my-*` en
            n'annulant qu'un côté. L'espacement vient du `gap` du parent. */}
        <Checkbox id="rester" name="rester" label="Rester connecté" defaultChecked ton="fonce" />

        {/* `min-h-[44px]` : la cible tactile du lien, alignée sur celle de la
            case à côté de lui. La rangée fait déjà 44px, elle ne bouge donc pas. */}
        <LienVerre href="/mot-de-passe-oublie" className="inline-flex min-h-[44px] items-center text-[13px]">
          Mot de passe oublié ?
        </LienVerre>
      </div>

      {/* Erreur générale : celle qui ne vise aucun champ en particulier.

          Le bloc reste **clair** sur la carte sombre, sans un mot de changé : le
          couple corail `alert-bg` / `alert-fg` est fait l'un pour l'autre
          (5,76:1) et il se suffit, quel que soit ce qu'il y a dessous. C'est
          d'ailleurs ce que fait la maquette de son bloc de confirmation. La
          bascule vers `text-alert-bg` que documente `Field.tsx` ne concerne que
          le texte d'erreur posé **à nu** sur le verre, sans fond à lui. */}
      {etat.erreur && !etat.champ && (
        <p
          role="alert"
          className="rounded-card-sm border border-alert-border bg-alert-bg px-4 py-3 text-[13.5px] font-medium text-alert-fg"
        >
          {etat.erreur}
        </p>
      )}

      {/* `onInk` : la variante que le kit prévoit déjà pour les fonds sombres —
          la lavande devient la surface, l'encre le texte (5,18:1). On ne la
          double pas. Son échelle est celle de l'app, pas celle de la maquette :
          46px de haut, donc la cible tactile de 44px est tenue. */}
      <Button type="submit" variant="onInk" disabled={enCours} className="w-full">
        {enCours ? (
          <>
            <Pending label="Connexion en cours" /> Connexion…
          </>
        ) : (
          'Se connecter'
        )}
      </Button>

      {/*
        La maquette montre ici un « Continuer avec Google » grisé, marqué
        « phase 2 ». Il n'est pas repris : Baptiste a tranché « pas de bouton
        inactif », et cet arbitrage est postérieur au handoff. Un bouton qui ne
        fait rien occupe la place, appelle le clic et n'apprend rien — annoncer
        une fonction absente n'est pas la rendre disponible. Il reviendra le jour
        où il marchera.
      */}
    </form>
  )
}

/**
 * Les trois états de l'indicateur, repris de la maquette.
 *
 * ⚠️ **Les libellés ne bougent pas d'un caractère** — en particulier celui de
 * l'accès interne, qui s'arrête volontairement avant de nommer le rôle. Seules
 * les classes changent, pour le ton sombre de la carte de verre.
 *
 * ⚠️ Ce sont des chaînes **littérales** en table, jamais construites par
 * interpolation : Tailwind ne génère que ce qu'il lit tel quel dans le fichier.
 */
function decrireAiguillage(email: string) {
  if (!email.includes('@')) {
    return {
      titre: 'En attente de votre adresse',
      detail: 'L’espace est attribué automatiquement — vous n’avez rien à choisir.',
      classe: 'border-cream/20 bg-cream/5',
      point: 'bg-cream/45',
    }
  }

  if (adresseInterne(email)) {
    return {
      titre: 'Accès équipe Umiros',
      // On s'arrête là : le rôle exact est porté par le compte et se découvre
      // après authentification, pas avant.
      detail: 'Adresse du domaine Umiros — votre espace interne s’ouvrira à la connexion.',
      classe: 'border-[1.5px] border-lavender bg-lavender/15',
      point: 'bg-lavender',
    }
  }

  return {
    titre: 'Accès apprenant',
    detail: 'Espace client — replays, supports, agenda et documents de votre parcours.',
    classe: 'border-cream/25 bg-cream/12',
    point: 'bg-cream',
  }
}
